import type { Signature } from '@/types/signature';
import type { NetworkId } from '@mls-wallet/shared-types';

interface GetSignatureCountsAndUserVoteInputs {
  approvals: Signature[];
  rejections: Signature[];
  userId: string | undefined;
  requiredNetworkIds: NetworkId[];
}

interface SignatureCountsAndUserVote {
  numApprovals: number;
  numRejections: number;
  hasApproved: boolean;
  hasRejected: boolean;
  userVote: 'approved' | 'rejected' | undefined;
}

/**
 * Normalizes the fields that determine batch readiness across the different
 * proposal API response shapes.
 */
export interface ProposalExecutionCandidate {
  id: string;
  status: string | null | undefined;
  approvals: Signature[];
  rejections: Signature[];
  requiredApprovals: number;
  requiredNetworkIds: NetworkId[];
}

/**
 * Carries only IDs that passed validation so dialogs never execute unchecked
 * UI selection state.
 */
export interface SelectedProposalExecutionState {
  selectedProposalIds: string[];
  canExecuteApproval: boolean;
  canExecuteRejection: boolean;
}

/**
 * Returns true if the given signatures cover all required network IDs
 * (i.e., the user has signed on every required network).
 */
function hasSignedAllRequiredNetworks(
  signatures: Signature[],
  networkIds: NetworkId[]
): boolean {
  const signedNetworkIds = new Set<NetworkId>();
  for (const { signature, networkId } of signatures) {
    if (signature && networkId !== undefined) {
      signedNetworkIds.add(networkId);
    }
  }

  return networkIds.every((networkId) => signedNetworkIds.has(networkId));
}

/**
 * Groups signatures by user and counts how many users have signed on
 * all required networks.
 */
function countNetworkCompleteSigners(
  signatures: Signature[],
  networkIds: NetworkId[]
): number {
  const signaturesByUser = new Map<string, Signature[]>();
  for (const signature of signatures) {
    const existing = signaturesByUser.get(signature.reviewingUserId) ?? [];
    existing.push(signature);
    signaturesByUser.set(signature.reviewingUserId, existing);
  }

  return [...signaturesByUser.values()].filter((userSignatures) =>
    hasSignedAllRequiredNetworks(userSignatures, networkIds)
  ).length;
}

/**
 * Count unique admins with completed on-device signatures.
 * Use for single-network org-operation proposals (e.g. disaster recovery).
 */
export function countCompletedSigners(signatures: Signature[]): number {
  const completedUserIds = new Set<string>();
  for (const signature of signatures) {
    if (signature.signature !== null) {
      completedUserIds.add(signature.reviewingUserId);
    }
  }
  return completedUserIds.size;
}

interface OrgProposalSignatureCountsAndUserVoteInputs {
  approvals: Signature[];
  rejections: Signature[];
  userId: string | undefined;
}

/**
 * Tallies and user vote for org-operation proposals without per-network signature rows.
 * Pending rows (signature: null) are excluded from counts and hasApproved/hasRejected.
 */
export function getOrgProposalSignatureCountsAndUserVote({
  approvals,
  rejections,
  userId,
}: OrgProposalSignatureCountsAndUserVoteInputs) {
  const numApprovals = countCompletedSigners(approvals);
  const numRejections = countCompletedSigners(rejections);

  let hasApproved = false;
  let hasRejected = false;
  if (userId) {
    hasApproved = approvals.some(
      (signature) =>
        signature.reviewingUserId === userId && signature.signature !== null
    );
    hasRejected = rejections.some(
      (signature) =>
        signature.reviewingUserId === userId && signature.signature !== null
    );
  }

  return {
    numApprovals,
    numRejections,
    hasApproved,
    hasRejected,
  };
}

/**
 * Get approval count, rejection count, and user's vote status for a proposal
 * NOTE: This is made more complex by the fact that: for a particular user, a signable can have multiple signatures
 * across different networks. So we need to deduplicate signatures by [proposalId and decision] to get accurate counts.
 */
export function getSignatureCountsAndUserVote({
  approvals,
  rejections,
  userId,
  requiredNetworkIds,
}: GetSignatureCountsAndUserVoteInputs): SignatureCountsAndUserVote {
  const numApprovals = countNetworkCompleteSigners(
    approvals,
    requiredNetworkIds
  );

  const numRejections = countNetworkCompleteSigners(
    rejections,
    requiredNetworkIds
  );

  // Determine user's vote - If they have approved all or rejected all signatures, set their vote accordingly
  let hasApproved = false;
  let hasRejected = false;
  let userVote: 'approved' | 'rejected' | undefined;
  if (userId) {
    const usersApprovals = approvals.filter(
      (s: Signature) => s.reviewingUserId === userId
    );
    hasApproved =
      usersApprovals.length > 0 &&
      hasSignedAllRequiredNetworks(usersApprovals, requiredNetworkIds);
    const usersRejections = rejections.filter(
      (s: Signature) => s.reviewingUserId === userId
    );
    hasRejected =
      usersRejections.length > 0 &&
      hasSignedAllRequiredNetworks(usersRejections, requiredNetworkIds);
    if (hasApproved) userVote = 'approved';
    else if (hasRejected) userVote = 'rejected';
  }

  return {
    numApprovals,
    numRejections,
    hasApproved,
    hasRejected,
    userVote,
  };
}

type CanExecuteApprovalInputs = {
  status: string | null | undefined;
  numApprovals: number;
  requiredApprovals: number | null | undefined;
};

/** Returns true when a pending proposal has enough completed approvals. */
export function canExecuteApproval({
  status,
  numApprovals,
  requiredApprovals,
}: CanExecuteApprovalInputs): boolean {
  return status === 'PENDING' && numApprovals >= (requiredApprovals ?? 1);
}

type CanExecuteRejectionInputs = {
  status: string | null | undefined;
  numRejections: number;
  requiredApprovals: number | null | undefined;
};

/**
 * Shared helper for determining if a proposal can be executed with REJECT.
 * We standardize this logic in one place since it appears across multiple proposal UIs.
 */
export function canExecuteRejection({
  status,
  numRejections,
  requiredApprovals,
}: CanExecuteRejectionInputs): boolean {
  return status === 'PENDING' && numRejections >= (requiredApprovals ?? 1);
}

/**
 * Derives batch execution readiness from polled proposals and the UI selection.
 *
 * Every selected ID must resolve exactly once. Approval or rejection execution
 * is enabled only when all selected proposals remain pending and meet the
 * corresponding signature threshold.
 */
export function getSelectedProposalExecutionState(
  proposals: ProposalExecutionCandidate[],
  selectedIds: string[]
): SelectedProposalExecutionState {
  // Polling can remove a proposal while its ID remains selected. Require a
  // one-to-one match so stale UI state cannot execute an unexpected subset.
  const selectedIdSet = new Set(selectedIds);
  const selectedProposals = proposals.filter((proposal) =>
    selectedIdSet.has(proposal.id)
  );
  // An empty selection has no executable batch.
  const hasSelectedProposals = selectedIds.length > 0;
  // Repeated IDs could otherwise register the same proposal more than once.
  const hasUniqueSelection = selectedIdSet.size === selectedIds.length;
  // Polling may leave selected IDs that no longer resolve to proposals.
  const resolvedEverySelectedProposal =
    selectedProposals.length === selectedIdSet.size;
  const hasCompleteSelection =
    hasSelectedProposals && hasUniqueSelection && resolvedEverySelectedProposal;

  // Case: Reject an empty, duplicated, or stale batch before deriving
  // executable proposal IDs.
  if (!hasCompleteSelection) {
    return {
      selectedProposalIds: [],
      canExecuteApproval: false,
      canExecuteRejection: false,
    };
  }

  // An administrator counts toward the threshold only after signing every
  // network required by that proposal.
  const proposalCounts = selectedProposals.map((proposal) => ({
    proposal,
    counts: getSignatureCountsAndUserVote({
      approvals: proposal.approvals,
      rejections: proposal.rejections,
      userId: undefined,
      requiredNetworkIds: proposal.requiredNetworkIds,
    }),
  }));

  return {
    selectedProposalIds: selectedProposals.map((proposal) => proposal.id),
    canExecuteApproval: proposalCounts.every(({ proposal, counts }) =>
      canExecuteApproval({
        status: proposal.status,
        numApprovals: counts.numApprovals,
        requiredApprovals: proposal.requiredApprovals,
      })
    ),
    canExecuteRejection: proposalCounts.every(({ proposal, counts }) =>
      canExecuteRejection({
        status: proposal.status,
        numRejections: counts.numRejections,
        requiredApprovals: proposal.requiredApprovals,
      })
    ),
  };
}
